import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, Clock, MapPin, ArrowUpRight, MessageCircle, Navigation } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT_INFO } from "@/i18n/translations";
import { CONTACT } from "@/constants/testIds/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const { lat, lng } = CONTACT_INFO.coords;
// Small bounding box around the point for the OSM embed
const D = 0.005;
const MAP_EMBED_SRC = `https://www.openstreetmap.org/export/embed.html?bbox=${lng - D}%2C${lat - D}%2C${lng + D}%2C${lat + D}&layer=mapnik&marker=${lat}%2C${lng}`;
const MAP_LINK = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
const WA_LINK = `https://wa.me/${CONTACT_INFO.whatsappRaw}`;

export default function Contact() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, language: lang });
      if (res.data?.status === "success" || res.data?.status === "saved") {
        setStatus("success");
        toast.success(t.contact.success);
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        setStatus("error");
        toast.error(t.contact.error);
      }
    } catch (err) {
      console.error(err);
      setStatus("error");
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-[#1A1A1A]/20 focus:border-[#8B1A1A] outline-none rounded-none py-3 text-[#1A1A1A] placeholder:text-[#5A5A5A]/60 transition-colors";

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="py-20 md:py-32 border-t border-[#1A1A1A]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-[#8B1A1A] mb-6">
              {t.contact.eyebrow}
            </p>
            <h2 className="font-serif font-light text-[#1A1A1A] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-[#5A5A5A] text-base md:text-lg max-w-md leading-relaxed">
              {t.contact.lede}
            </p>

            <div className="mt-12 space-y-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#5A5A5A]">
                {t.contact.infoTitle}
              </p>
              <a
                data-testid="contact-info-email"
                href={`mailto:${t.contact.email}`}
                className="flex items-center gap-4 text-[#1A1A1A] hover:text-[#8B1A1A] transition-colors group"
              >
                <Mail className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span className="font-serif text-xl md:text-2xl">{t.contact.email}</span>
                <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a
                data-testid="contact-info-phone"
                href={`tel:${CONTACT_INFO.phoneRaw}`}
                className="flex items-center gap-4 text-[#1A1A1A] hover:text-[#8B1A1A] transition-colors"
              >
                <Phone className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span className="font-serif text-xl md:text-2xl">{t.contact.phone}</span>
              </a>
              <a
                data-testid="contact-info-whatsapp"
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-[#1A1A1A] hover:text-[#8B1A1A] transition-colors group"
              >
                <MessageCircle className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span className="font-serif text-xl md:text-2xl">{t.contact.whatsapp}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#8B1A1A] border border-[#8B1A1A]/40 px-2 py-0.5">
                  {t.contact.whatsappLabel}
                </span>
                <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <div className="flex items-center gap-4 text-[#5A5A5A]">
                <Clock className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span>{t.contact.hours}</span>
              </div>
              <a
                data-testid="contact-info-map-link"
                href={MAP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-[#1A1A1A] hover:text-[#8B1A1A] transition-colors group"
              >
                <MapPin className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span>
                  {CONTACT_INFO.coords.lat.toFixed(6)}, {CONTACT_INFO.coords.lng.toFixed(6)}
                </span>
                <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>

          <motion.form
            data-testid={CONTACT.form}
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
          >
            <div className="md:col-span-1">
              <label className="text-xs uppercase tracking-[0.2em] text-[#5A5A5A]">
                {t.contact.fields.name}
              </label>
              <input
                required
                data-testid={CONTACT.name}
                type="text"
                value={form.name}
                onChange={onChange("name")}
                placeholder={t.contact.placeholders.name}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-xs uppercase tracking-[0.2em] text-[#5A5A5A]">
                {t.contact.fields.email}
              </label>
              <input
                required
                data-testid={CONTACT.email}
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder={t.contact.placeholders.email}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-xs uppercase tracking-[0.2em] text-[#5A5A5A]">
                {t.contact.fields.phone}
              </label>
              <input
                data-testid={CONTACT.phone}
                type="tel"
                value={form.phone}
                onChange={onChange("phone")}
                placeholder={t.contact.placeholders.phone}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-xs uppercase tracking-[0.2em] text-[#5A5A5A]">
                {t.contact.fields.subject}
              </label>
              <input
                data-testid={CONTACT.subject}
                type="text"
                value={form.subject}
                onChange={onChange("subject")}
                placeholder={t.contact.placeholders.subject}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[#5A5A5A]">
                {t.contact.fields.message}
              </label>
              <textarea
                required
                data-testid={CONTACT.message}
                value={form.message}
                onChange={onChange("message")}
                placeholder={t.contact.placeholders.message}
                rows={5}
                className={`${inputCls} resize-none`}
              />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
              <div aria-live="polite" className="min-h-[24px]">
                {status === "success" && (
                  <span data-testid={CONTACT.success} className="text-sm text-[#8B1A1A]">
                    {t.contact.success}
                  </span>
                )}
                {status === "error" && (
                  <span data-testid={CONTACT.error} className="text-sm text-[#8B1A1A]">
                    {t.contact.error}
                  </span>
                )}
              </div>
              <button
                data-testid={CONTACT.submit}
                type="submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-[#8B1A1A] text-white text-xs uppercase tracking-[0.24em] px-8 py-4 hover:bg-[#1A1A1A] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? t.contact.submitting : t.contact.submit}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </div>
          </motion.form>
        </div>

        {/* Map */}
        <motion.div
          data-testid="contact-map"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mt-20 md:mt-28 border-t border-[#1A1A1A]/10 pt-12"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <p className="font-sans uppercase tracking-[0.28em] text-xs text-[#8B1A1A] mb-3">
                {t.contact.mapLabel}
              </p>
              <h3 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">
                {CONTACT_INFO.coords.lat.toFixed(6)}, {CONTACT_INFO.coords.lng.toFixed(6)}
              </h3>
            </div>
            <a
              data-testid="contact-map-directions"
              href={MAP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#1A1A1A] hover:text-[#8B1A1A] transition-colors border border-[#1A1A1A]/20 hover:border-[#8B1A1A] px-5 py-3"
            >
              <Navigation className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t.contact.directions}
            </a>
          </div>
          <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden border border-[#1A1A1A]/10">
            <iframe
              title="Decrepalma — Localização"
              src={MAP_EMBED_SRC}
              className="absolute inset-0 w-full h-full grayscale-[30%] hover:grayscale-0 transition-all duration-500"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
